
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  layout: "guide",
  description: "Learn test values to ensure you always pass a session."
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title="Testing in Sandbox" subtitle="Test values and options for Identity Verification" mdxType="Header" />
    <Callout mdxType="Callout">
      <p>{`Identity Verification is not enabled by default in the Sandbox environment. To obtain Sandbox access, go to the `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/settings/team/products"
        }}>{`Products page in the Dashboard`}</a>{` and click "Enable Sandbox" under Identity Verification.`}</p>
    </Callout>
    <h4 {...{
      "id": "sandbox-users-for-identity-verification"
    }}>{`Sandbox users for Identity Verification`}</h4>
    <h5 {...{
      "id": "primary-test-user-leslie-knope"
    }}>{`Primary test user (Leslie Knope)`}</h5>
    <p>{`In Sandbox mode, Identity Verification accepts the fixed set of inputs below in order to result in successful Data Source and Documentary Verification checks.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Form Field`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Test Value`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Mobile number`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`+1 234-567-8909`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`First name`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Leslie`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Last name`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Knope`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Verification code`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`11111`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Address`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`123 Main St.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`City`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Pawnee`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`State`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Indiana`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`ZIP code`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`46001`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Month`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`January`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Day`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`18`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Year`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`1975`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`SSN`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`123-45-6788 or 123-45-6789`}</td>
        </tr>
      </tbody>
    </table>
    <h5 {...{
      "id": "custom-test-users"
    }}>{`Custom test users`}</h5>
    <p>{`Plaid supports creating custom test users from your organization directly in Sandbox. Custom test users can be managed on the Identity Verification page in the Dashboard, under `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/sandbox/identity_verification/sample_identities"
      }}>{`Sample Identities`}</a>{`, allowing you to test your own sets of PII to result in successful Data Source and Documentary Verification checks.`}</p>
    <p>{`Sample Identities are specific to Identity Verification. For bank account test data, see `}<a parentName="p" {...{
        "href": "/docs/sandbox/user-custom/"
      }}>{`custom Sandbox users`}</a>{`; for testing flows that recognize a returning user, see `}<a parentName="p" {...{
        "href": "/docs/sandbox/profiles/"
      }}>{`Sandbox Profiles`}</a>{`.`}</p>
    <p>{`The following names are reserved and cannot be used for custom sample identities:`}</p>
    <ul>
      <li parentName="ul">{`Leslie Knope`}</li>
      <li parentName="ul">{`Jerry Gergich (or any first name used with the surname Gergich, such as Larry Gergich, Terry Gergich, etc.)`}</li>
    </ul>
    <h4 {...{
      "id": "identity-verification-behavior-in-sandbox"
    }}>{`Identity Verification behavior in Sandbox`}</h4>
    <h5 {...{
      "id": "data-source-checks"
    }}>{`Data Source checks`}</h5>
    <p>{`Data Source checks in Sandbox will be compared against the information above. To simulate different test cases and results, you can adjust your Data Source input at the attribute level (e.g. an incorrect birthdate), and set up different Identity Rules to simulate different verification results.`}</p>
    <h5 {...{
      "id": "document-checks"
    }}>{`Document checks`}</h5>
    <p>{`Documents uploaded in Sandbox will always be interpreted as genuine (not fake) documents reflecting the name and date of birth matching the Leslie Knope test user. Document check will pass if the data provided in the Data Source check matches this name and date of birth, and will fail otherwise.`}</p>
    <p>{`Users have three attempts to pass the Document check, so the status will not be updated in the Dashboard to `}<inlineCode parentName="p">{`failed`}</inlineCode>{` until you have provided mismatching data three times.`}</p>
    <h5 {...{
      "id": "selfie-checks"
    }}>{`Selfie checks`}</h5>
    <p>{`Selfie checks will not be run in Sandbox mode, even if they are enabled in your template.`}</p>
    <h5 {...{
      "id": "aml-screening"
    }}>{`AML Screening`}</h5>
    <p>{`In Sandbox, Monitor screens against a real-world dataset, so the Sandbox user will not return any screening hits. For more information, including example data you can use to trigger screening hits, see `}<a parentName="p" {...{
        "href": "/docs/monitor/#testing-monitor"
      }}>{`Testing Monitor`}</a>{`.`}</p>
    <h5 {...{
      "id": "risk-check"
    }}>{`Risk Check`}</h5>
    <p>{`The risk check is fully functional in Sandbox. It's common to trigger high risk scores when testing. For more information and advice, see `}<a parentName="p" {...{
        "href": "#risk-rules-for-testing"
      }}>{`Risk rules for testing`}</a>{`.`}</p>
    <h5 {...{
      "id": "auto-fill"
    }}>{`Auto-fill`}</h5>
    <p>{`Auto-fill behavior is fully functional in Sandbox.`}</p>
    <h5 {...{
      "id": "financial-account-matching"
    }}>{`Financial Account Matching`}</h5>
    <p>{`Financial Account Matching is fully functional in Sandbox. If you want to change the Identity data that is being compared to the Identity Verification session, see `}<a parentName="p" {...{
        "href": "/docs/sandbox/user-custom"
      }}>{`how to create Sandbox test data`}</a>{`.`}</p>
    <h4 {...{
      "id": "risk-rules-for-testing"
    }}>{`Risk rules for testing`}</h4>
    <p>{`Many common testing behaviors (e.g. attempting to enter the Identity Verification flow repeatedly on the same device using different credentials) may be flagged as risky behavior and cause your verification attempt to fail. For testing purposes, you can temporarily set the Acceptable Risk Level for any checks you are failing (typically Network Risk and Device Risk) to `}<strong parentName="p">{`High`}</strong>{`, under the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/identity_verification/templates"
      }}><strong parentName="a">{`Rulesets -> Risk Rules`}</strong></a>{` section of the template editor. Make sure to set the Acceptable Risk Level back to your desired setting before launching in Production.`}</p>
    <p>{`If you want to force a check to fail in Sandbox, an easy way to do this is to set Acceptable Risk Level for the Network Risk and Device Risk fields to `}<strong parentName="p">{`Low`}</strong>{`. Checks will begin failing after your first few attempts.`}</p>
    <h4 {...{
      "id": "triggering-specific-test-outcomes"
    }}>{`Triggering specific test outcomes`}</h4>
    <p>{`Using the rules above, it is easy to create the outcome of passing both Data Source checks and Document checks, or of failing both checks. If you need to simulate specific combinations of pass / fail (passing one check but not the other), you can use the guides below.`}</p>
    <h5 {...{
      "id": "data-source-check-passes-documentary-verification-check-fails"
    }}>{`Data Source check passes, Documentary Verification check fails`}</h5>
    <ol>
      <li parentName="ol">{`Set up your template to always be enabled for Data Source checks and Document Checks.`}</li>
      <li parentName="ol">{`Set up your template to not require a match or partial match on name or date of birth.`}</li>
      <li parentName="ol">{`Enter Leslie Knope's correct test data, or a sample identity you have configured in the Dashboard, for the address, SSN, and phone number fields.`}</li>
      <li parentName="ol">{`Enter incorrect data for the name and date of birth fields.`}</li>
    </ol>
    <p>{`If you would like this test to also trigger a Monitor hit, you can use the name and date of birth of a sanctioned individual. For examples, see `}<a parentName="p" {...{
        "href": "/docs/monitor/#testing-monitor"
      }}>{`Monitor test data`}</a>{`.`}</p>
    <h5 {...{
      "id": "data-source-check-fails-documentary-verification-check-passes"
    }}>{`Data Source check fails, Documentary Verification check passes`}</h5>
    <ol>
      <li parentName="ol">{`Set up your template to be enabled for Data Source checks, with Document checks as a fallback.`}</li>
      <li parentName="ol">{`Set up your template to require a match for address, SSN, and phone number fields.`}</li>
      <li parentName="ol">{`Enter Leslie Knope's correct test data, or a sample identity you have configured in the Dashboard, for the name and date of birth fields.`}</li>
      <li parentName="ol">{`Enter incorrect data for the address, SSN, and phone number fields.`}</li>
    </ol>
    <h4 {...{
      "id": "testing-financial-account-matching"
    }}>{`Testing Financial Account Matching`}</h4>
    <ol>
      <li parentName="ol">{`Create a template with Financial Account Matching enabled.`}</li>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>{` with `}<inlineCode parentName="li">{`identity_verification`}</inlineCode>{` in the `}<inlineCode parentName="li">{`products`}</inlineCode>{` array. Make sure to save the `}<inlineCode parentName="li">{`client_user_id`}</inlineCode>{` you are using with this call, as you will need it later.`}</li>
      <li parentName="ol">{`Launch Link and complete the Identity Verification session, entering Leslie Knope's correct test data.`}</li>
      <li parentName="ol">{`See the `}<a parentName="li" {...{
          "href": "https://github.com/plaid/sandbox-custom-users/tree/main"
        }}>{`Sandbox custom user repo`}</a>{` for instructions on configuring custom Sandbox users and create a custom user using the `}<a parentName="li" {...{
          "href": "https://github.com/plaid/sandbox-custom-users/blob/main/identity/leslie_knope_financial_account_matching.json"
        }}>{`Financial Account Matching custom user data`}</a>{` from that repo.`}</li>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>{` with `}<inlineCode parentName="li">{`identity`}</inlineCode>{` (or `}<inlineCode parentName="li">{`assets`}</inlineCode>{`, for underwriting use cases) in the `}<inlineCode parentName="li">{`products`}</inlineCode>{` array and the same `}<inlineCode parentName="li">{`client_user_id`}</inlineCode>{` as was used in the first step, and launch a Link session.`}</li>
      <li parentName="ol">{`Use the custom user you created above to log in to Link, then call `}<inlineCode parentName="li">{`/item/public_token/exchange`}</inlineCode>{` to exchange the public token for an access token.`}</li>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/identity/get`}</inlineCode>{` or `}<inlineCode parentName="li">{`/identity/match`}</inlineCode>{` (or `}<inlineCode parentName="li">{`/asset_report/create`}</inlineCode>{`, if using `}<inlineCode parentName="li">{`assets`}</inlineCode>{` instead of `}<inlineCode parentName="li">{`identity`}</inlineCode>{`) on the access token.`}</li>
      <li parentName="ol">{`Check the Identity Verification session in the Dashboard. You should see the linked account and a successful match in the Financial Account Matching section.`}</li>
    </ol>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "sandbox-users-for-identity-verification",
        level: 4,
        title: "Sandbox users for Identity Verification",
      },
{
        id: "identity-verification-behavior-in-sandbox",
        level: 4,
        title: "Identity Verification behavior in Sandbox",
      },
{
        id: "risk-rules-for-testing",
        level: 4,
        title: "Risk rules for testing",
      },
{
        id: "triggering-specific-test-outcomes",
        level: 4,
        title: "Triggering specific test outcomes",
      },
{
        id: "testing-financial-account-matching",
        level: 4,
        title: "Testing Financial Account Matching",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
